/**
 * Раздел «Закупка → Заказы»: общие типы и подписи — ступени воронки,
 * комментарии CRM, карго-накладная заказа, деньги и даты строкой.
 *
 * Спека: `docs/specs/2026-09-21-orders-list-design.md`. Наружу всё это уходит
 * фасадом `@/lib/orders`; здесь — то, на что опираются и список, и карточка,
 * и накладные.
 */

import { apiFetch } from "@/lib/api";
import { nf, num } from "@/lib/format";

/** Миниатюра ленты: товар площадки (или модель) × штук по всем размерам. */
export type StripItem = {
  product_id: number | null;
  model_code: string | null;
  qty: number;
  sizes: string | null;
  photo: string | null;
};

/** Карго-накладная заказа: номер, дата документа и «поехала» по всем её заказам. */
export type CargoRef = {
  media_file_id: number;
  code: string | null;
  doc_date: string | null;
  sent_min: string | null;
  sent_max: string | null;
};

export type CommentMark = { count: number; last: string | null };

/** Расхождение группы заказов, связанных общими платежами; `null` — сошлось. */
export type PayMismatch = { group_order_ids: number[]; paid: number; due: number; diff: number };

export type OrderComment = { comment_id: number; text: string; at: string | null; user: string | null };

/** К чему пишем свой комментарий в CRM — белый список `comment/add.py::OBJECTS`. */
export type CommentObject = "position" | "order";

/** Свой комментарий в CRM той же записью, что поле комментария CRM. */
export function addComment(object: CommentObject, id: number, text: string) {
  return apiFetch<OrderComment>("/api/comments", {
    method: "POST",
    body: JSON.stringify({ object, id, text }),
  });
}

/** Коды ступеней воронки — порядок и подписи приезжают с сервера. */
export type StageCode = "cargo_wait" | "china" | "to_rf" | "in_rf" | "priem" | "defect" | "other";

export type AnomalyCode = "fix" | "rf_but_created" | "closed_waiting" | "cargo_unknown_old";

/** Отметка воронки: ступень, дни на ней, норма, просрочка, аномалии. */
export type StageMark = {
  /** `null` — заказ закрыт или отменён. */
  stage: StageCode | null;
  stage_days: number | null;
  stage_norm: number | null;
  overdue: boolean;
  anomalies: AnomalyCode[];
};

/** Подписи ступеней для строки списка — те же, что на плитках дашборда. */
export const STAGE_LABELS: Record<StageCode, string> = {
  cargo_wait: "Оформлен, ждём карго",
  china: "Получен в Китае",
  to_rf: "Едет в РФ",
  in_rf: "Доставка по РФ",
  priem: "Приёмка, не полностью принят",
  defect: "Принято с браком",
  other: "Получен, не закрыт",
};

export const ANOMALY_LABELS: Record<AnomalyCode, string> = {
  fix: "Статус «Исправить»",
  rf_but_created: "Получен в РФ, а статус «Создан»",
  closed_waiting: "Закрыт, но есть непринятые штуки",
  cargo_unknown_old: "Карго неизвестно дольше нормы",
};

/** Список плитки: ровно те заказы, что плитка посчитала. */
export function stageHref(code: StageCode, overdue = false): string {
  return `/orders?stage=${code}${overdue ? "&overdue=1" : ""}`;
}

export function anomalyHref(code: AnomalyCode): string {
  return `/orders?anomaly=${code}`;
}

/** Наша карточка товара — по товару площадки. */
export function productHref(productId: number): string {
  return `/product/${productId}`;
}

/** «12 дн» и «12 из 11 дн»; нет дней — прочерк. */
export function stageDays(mark: Pick<StageMark, "stage_days" | "stage_norm">): string {
  if (mark.stage_days === null) return "—";
  return mark.stage_norm === null ? `${mark.stage_days} дн` : `${mark.stage_days} из ${mark.stage_norm} дн`;
}

/** Число или ПРОЧЕРК — общий `lib/format`; реэкспорт держит прежние импорты. */
export { num };

/** Деньги без копеек: юани в закупке читают тысячами, копейка тут шум. */
export function money(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return nf.format(Math.round(value));
}

/** `2026-07-25T14:53:17` → `25.07.26`; пусто → прочерк. */
export function day(value: string | null | undefined): string {
  if (!value) return "—";
  const [г, м, д] = (value.split("T")[0] ?? "").split("-");
  return г && м && д ? `${д}.${м}.${г.slice(2)}` : value;
}

/** Дата без года для метки: `30.10` из `2026-10-30 06:30`, `2026-10-30T06:30` или `2026-10-30`. */
export function dayShort(value: string | null | undefined): string {
  if (!value) return "—";
  const [г, м, д] = (value.split(/[T ]/)[0] ?? "").split("-");
  return г && м && д ? `${д}.${м}` : "—";
}

/** «Поехала»: один день — `12.08.26`, диапазон — `12–14.08` или `30.07–02.08`. */
export function sentLabel(c: Pick<CargoRef, "sent_min" | "sent_max">): string {
  if (!c.sent_min || !c.sent_max) return "—";
  if (c.sent_min === c.sent_max) return day(c.sent_min);
  const [, м1, д1] = c.sent_min.split("-");
  const [, м2, д2] = c.sent_max.split("-");
  return м1 === м2 ? `${д1}–${д2}.${м2}` : `${д1}.${м1}–${д2}.${м2}`;
}

/** Заказы накладной «поехали» в разные дни — повод присмотреться. */
export function sentSplit(c: Pick<CargoRef, "sent_min" | "sent_max">): boolean {
  return Boolean(c.sent_min && c.sent_max && c.sent_min !== c.sent_max);
}

/** Подсказка красной ячейки «оплачено»: обе суммы, разница и состав группы. */
export function mismatchNote(m: PayMismatch): string {
  const знак = m.diff > 0 ? "+" : "−";
  const база = `оплачено ${money(m.paid)} ¥, к оплате ${money(m.due)} ¥ (товар + доставка), разница ${знак}${money(Math.abs(m.diff))} ¥`;
  return m.group_order_ids.length > 1
    ? `${база} · общие платежи у заказов ${m.group_order_ids.join(", ")}`
    : база;
}
